// src/pages/RegisterPage.jsx
import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Text, Input, Button, VStack, IconButton } from "@chakra-ui/react";
import { LuEye, LuEyeOff } from "react-icons/lu";
import AuthContext from "../context/AuthContext";
import AuthPage from "../components/AuthPage";

export default function RegisterPage() {
  const { register, requestAccess, loadingAuth, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({ email: "", password: "", confirm: "" });
  const [errors, setErrors] = useState({});
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const isEnvarisEmail = formData.email.toLowerCase().endsWith("@envaris.de");

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = "E-Mail ist erforderlich.";
    if (!formData.password.trim()) newErrors.password = "Passwort ist erforderlich.";
    if (!formData.confirm.trim()) newErrors.confirm = "Bitte Passwort bestätigen.";
    if (!newErrors.password && !newErrors.confirm && formData.password !== formData.confirm) {
      newErrors.confirm = "Passwörter stimmen nicht überein.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (isEnvarisEmail) {
    const success = await register(formData.email, formData.password);
    if (success) navigate("/login");
    } else {
      const success = await requestAccess(formData.email, formData.password);
    if (success) navigate("/login");
    }
  };

  const inputProps = {
    bg: "blue.200",
    color: "white",
    _placeholder: { color: "whiteAlpha.800" },
    borderColor: "whiteAlpha.700",
    _hover: { borderColor: "whiteAlpha.900" },
    _focus: { bg: "blue.200", borderColor: "white", color: "white" },
    _light: { color: "black", _placeholder: { color: "blackAlpha.500" } },
  };

  return (
    <AuthPage
      title="📝 Registrierung"
      subtitle="Erstelle dein Konto. Die Freigabe durch einen Admin kann erforderlich sein."
    >
      <form onSubmit={handleSubmit}>
        <VStack gap={4} align="stretch">

          {/* Email */}
          <Box>
            <Text fontWeight="semibold" mb={1}>E-Mail</Text>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="dein@email.de"
              value={formData.email}
              onChange={handleChange}
              autoComplete="email"
              {...inputProps}
            />
            {errors.email && (
              <Text color="red.500" fontSize="sm">{errors.email}</Text>
            )}
          </Box>

          {/* Password */}
          <Box>
            <Text fontWeight="semibold" mb={1}>Passwort</Text>
            <Box position="relative">
              <Input
                id="password"
                name="password"
                type={showPw ? "text" : "password"}
                placeholder="••••••••"
                value={formData.password}
                onChange={handleChange}
                autoComplete="new-password"
                pr="2.5rem"
                {...inputProps}
              />
              <IconButton
                aria-label={showPw ? "Passwort verbergen" : "Passwort anzeigen"}
                size="sm"
                variant="ghost"
                icon={showPw ? <LuEyeOff /> : <LuEye />}
                onClick={() => setShowPw((s) => !s)}
                position="absolute"
                right="0.5rem"
                top="50%"
                transform="translateY(-50%)"
              />
            </Box>
            {errors.password && (
              <Text color="red.500" fontSize="sm">{errors.password}</Text>
            )}
          </Box>

          {/* Confirm Password */}
          <Box>
            <Text fontWeight="semibold" mb={1}>Passwort bestätigen</Text>
            <Box position="relative">
              <Input
                id="confirm"
                name="confirm"
                type={showConfirm ? "text" : "password"}
                placeholder="••••••••"
                value={formData.confirm}
                onChange={handleChange}
                autoComplete="new-password"
                pr="2.5rem"
                {...inputProps}
              />
              <IconButton
                aria-label={showConfirm ? "Passwort verbergen" : "Passwort anzeigen"}
                size="sm"
                variant="ghost"
                icon={showConfirm ? <LuEyeOff /> : <LuEye />}
                onClick={() => setShowConfirm((s) => !s)}
                position="absolute"
                right="0.5rem"
                top="50%"
                transform="translateY(-50%)"
              />
            </Box>
            {errors.confirm && (
              <Text color="red.500" fontSize="sm">{errors.confirm}</Text>
            )}
          </Box>

          {/* Envaris Registration */}
          <Button
            type="submit"
            colorScheme="blue"
            size="lg"
            isLoading={loadingAuth && isEnvarisEmail}
            isDisabled={!isEnvarisEmail}
            opacity={isEnvarisEmail ? 1 : 0.4}
          >
            Registrieren (@envaris.de)
          </Button>

          {/* External Access Request */}
          <Button
            type="submit"
            colorScheme="orange"
            size="lg"
            isLoading={loadingAuth && !isEnvarisEmail}
            isDisabled={isEnvarisEmail}
            opacity={isEnvarisEmail ? 0.4 : 1}
          >
            Anfrage stellen (Extern)
          </Button>

        </VStack>
      </form>
    </AuthPage>
  );
}